/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Field, Form, Formik } from 'formik';
import { useTranslation, Trans } from 'react-i18next';
import * as Yup from 'yup';
import i18n from '../../i18n';
import { randomPassword, readableProduct } from '../../utils/utils';
// Components
import Input from '../shared/atoms/Input';
import Button from '../shared/atoms/Button';
import Box from '../shared/atoms/Box';
import Text from '../shared/atoms/Text';
import Checkbox from '../shared/atoms/Checkbox';
import WrapperForm from './WrapperForm';
import Link from '../shared/atoms/Link';
// Icons
import UserAlt24 from '../icons/UserAlt24';
import Eye24 from '../icons/Eye24';
import Message24 from '../icons/Message24';
import Lock24 from '../icons/Lock24';
import Phone24 from '../icons/Phone24';
import AngleRight16 from '../icons/AngleRight16';
import GoogleColor24 from '../icons/GoogleColor24';
import { FeaturesContext } from '../../contexts/FeaturesContext';

const phoneRegExp = /^(0[67]\d{8}|\+\d{11})$/;
const signupValidationSchema = Yup.object().shape({
  password: Yup.string()
    .min(8, i18n.t('password.eightcharacterminimum'))
    .required(i18n.t('fieldrequired')),
  email: Yup.string()
    .email(i18n.t('email.invalid'))
    .required(i18n.t('fieldrequired')),
  prenom: Yup.string().required(i18n.t('fieldrequired')),
  nom: Yup.string().required(i18n.t('fieldrequired')),
  tel: Yup.string()
    .matches(phoneRegExp, i18n.t('phone.invalid'))
    .required(i18n.t('fieldrequired')),
});

const CHAMPS_IDENTITE = ['prenom', 'nom', 'tel', 'email'];

/** Rouvre les champs repliés quand l'un d'eux bloque l'envoi (un fixe refusé, par exemple). */
const OuvrirSiBloque = ({ bloque, ouvrir }) => {
  useEffect(() => {
    if (bloque) ouvrir();
  }, [bloque]);
  return null;
};

const Recap = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px 14px;
  border: 1px solid ${(p) => p.theme.colors.gray.main};
  border-radius: 12px;
  background: ${(p) => p.theme.colors.white};
  font-size: 0.9375em;
  line-height: 1.35;

  .identite {
    min-width: 0;
    overflow-wrap: anywhere;
  }
  .identite b {
    display: block;
  }
  .identite span {
    color: ${(p) => p.theme.colors.gray.darker};
  }
  button {
    flex: 0 0 auto;
    padding: 8px 4px;
    border: 0;
    background: none;
    font: inherit;
    font-weight: 600;
    color: ${(p) => p.theme.colors.blue.electric};
    text-decoration: underline;
    cursor: pointer;
  }
`;

/* Le bouton reste à portée du pouce : collé en bas de l'écran sur téléphone. */
const CtaCollant = styled.div`
  ${(p) =>
    p.$collant &&
    `
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin: 0 -16px;
    padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
    background: rgba(255, 255, 255, 0.96);
    box-shadow: 0 -8px 16px rgba(0, 0, 0, 0.06);
  `}
`;

const Depliant = styled.details`
  margin-top: 16px;
  font-size: 0.8125em;
  color: ${(p) => p.theme.colors.gray.darker};

  summary {
    cursor: pointer;
    padding: 6px 0;
    text-decoration: underline;
  }
`;

const SignupForm = ({
  product_id = null,
  hasTextCheckbox = false,
  text,
  linkText,
  withSwitch = true,
  withTitle,
  ctaLabel,
  bg,
  noPadding,
  isGoogleLoggingIn,
  onGoogleClick,
  googleError,
  withGoogle = true,
  withNewsletter = true,
  initialValues = {},
  compact = false,
  ctaSticky = false,
  sousBouton = null,
  ...props
}) => {
  const { t } = useTranslation();
  // Mode compact : les coordonnées déjà connues se replient en un récap, seul le
  // mot de passe reste à saisir. « Modifier » les rouvre.
  const [edition, setEdition] = useState(false);
  // Rien à replier tant que les quatre ne sont pas tous connus : sur la page sans
  // demande, le formulaire s'ouvre complet.
  const identiteConnue = CHAMPS_IDENTITE.every((champ) => !!initialValues[champ]);
  const replie = compact && !edition && identiteConnue;
  const { isFeatureOn } = useContext(FeaturesContext);

  return (
    <WrapperForm
      withTitle={withTitle === null ? null : withTitle || t('signupform.titles.1')}
      text={text || t('signupform.texts.1')}
      linkText={linkText || t('signupform.switchtologin')}
      withSwitch={withSwitch}
      chooseSwitch="LOGIN"
      bg={bg}
      noPadding={noPadding}
      {...props}>
      {withGoogle && googleError && (
        <Text mb={3} color="primary.darkest">
          {googleError}
        </Text>
      )}
      {withGoogle && (
        <Box mb={4}>
          <Button
            variant="filledWhite"
            fullWidth
            onClick={onGoogleClick}
            loading={isGoogleLoggingIn}
            data-testid="signup-google-button">
            <Box
              flexDirection="row"
              alignItems="center"
              justifyContent="center">
              <GoogleColor24 mr={2} aria-hidden />
              {t(
                'googleauth.login.cta',
                'Continuer avec Google',
              )}
            </Box>
          </Button>
        </Box>
      )}
      <Formik
        initialValues={{
          prenom: '',
          nom: '',
          tel: '',
          email: '',
          password: isFeatureOn('ISWIDGET') ? randomPassword() : '',
          nloptin: 0,
          ...initialValues,
        }}
        validationSchema={signupValidationSchema}
        onSubmit={props.submit}>
        {({ isSubmitting, values, setFieldValue, errors, submitCount }) => (
          <Form>
            {compact && (
              <OuvrirSiBloque
                bloque={
                  !!submitCount &&
                  CHAMPS_IDENTITE.some((champ) => !!errors[champ])
                }
                ouvrir={() => setEdition(true)}
              />
            )}
            {replie && (
              <Recap data-testid="signup-recap">
                <div className="identite">
                  <b>
                    {values.prenom} {values.nom}
                  </b>
                  <span>
                    {values.email} · {values.tel}
                  </span>
                </div>
                <button
                  type="button"
                  data-testid="signup-recap-modifier"
                  onClick={() => setEdition(true)}>
                  {t('espacesite.modifier')}
                </button>
              </Recap>
            )}
            <Box
              flexDirection={{ xs: 'column', lg: 'row' }}
              style={replie ? { display: 'none' } : undefined}>
              <Field
                name="prenom"
                render={({ field }) => (
                  <Input
                    mr={{ xs: 0, lg: 2 }}
                    style={{ flex: 1 }}
                    mb={{ xs: 2, lg: 4 }}
                    color="black"
                    data-testid="input-prenom"
                    bg="white"
                    before={<UserAlt24 />}
                    placeholder={t('firstname.inputlabel')}
                    error={!!submitCount && errors.prenom}
                    {...field}
                  />
                )}
              />
              <Field
                name="nom"
                render={({ field }) => (
                  <Input
                    ml={{ xs: 0, lg: 2 }}
                    style={{ flex: 1 }}
                    mb={{ xs: 2, lg: 4 }}
                    color="black"
                    data-testid="input-nom"
                    bg="white"
                    before={<UserAlt24 />}
                    placeholder={t('lastname.inputlabel')}
                    error={!!submitCount && errors.nom}
                    {...field}
                  />
                )}
              />
            </Box>
            <Field
              name="tel"
              render={({ field }) => (
                <Input
                  style={replie ? { display: 'none' } : undefined}
                  mb={{ xs: 2, lg: 4 }}
                  color="black"
                  bg="white"
                  data-testid="input-tel"
                  before={<Phone24 />}
                  placeholder={t('phone.inputlabel')}
                  error={!!submitCount && errors.tel}
                  {...field}
                />
              )}
            />
            <Field
              name="email"
              render={({ field }) => (
                <Input
                  style={replie ? { display: 'none' } : undefined}
                  mb={{ xs: 2, lg: 4 }}
                  color="black"
                  bg="white"
                  data-testid="input-email"
                  before={<Message24 />}
                  placeholder={t('email.inputlabel')}
                  error={!!submitCount && errors.email}
                  {...field}
                />
              )}
            />
            {!isFeatureOn('ISWIDGET') ? (
              <Field
                name="password"
                render={({ field }) => (
                  <Input
                    mb={{ xs: 2, lg: 4 }}
                    color="black"
                    bg="white"
                    type="password"
                    data-testid="input-password"
                    before={<Lock24 />}
                    after={<Eye24 />}
                    placeholder={t('password.inputlabel')}
                    error={!!submitCount && errors.password}
                    {...field}
                  />
                )}
              />
            ) : (
              <div />
            )}
            {!isFeatureOn('ISWIDGET') && withNewsletter ? (
              <Box
                mb={5}
                flexDirection="row"
                alignItems={{ xs: 'flex-start', lg: 'center' }}
                className="form-group">
                <Checkbox
                  id="nloptin"
                  type="checkbox"
                  className={'form-control'}
                  name={'doobl'}
                  checked={!!values.nloptin}
                  onChange={() =>
                    setFieldValue('nloptin', values.nloptin === 0 ? 1 : 0)
                  }
                />
                <Text
                  as="label"
                  fontWeight="500"
                  color="black"
                  variant="small"
                  htmlFor="nloptin"
                  ml={2}>
                  {hasTextCheckbox || (
                    <>
                      {t('signupform.newsletter.optin', {
                        label:
                          product_id &&
                          readableProduct(product_id, false, false, true),
                      })}
                    </>
                  )}
                </Text>
              </Box>
            ) : (
              <div />
            )}
            <CtaCollant $collant={ctaSticky}>
              <Box mb={ctaSticky ? 0 : 5}>
                <Button
                  after={<AngleRight16 />}
                  disabled={isSubmitting}
                  data-testid="signup-form-on-submit"
                  style={{ alignSelf: 'flex-end' }}
                  fullWidth
                  animation>
                  {ctaLabel || t('signup')}
                </Button>
              </Box>
            </CtaCollant>
            {sousBouton}
          </Form>
        )}
      </Formik>
      {(() => {
        const confidentialite = (
          <Text color="gray.darker" variant="caption">
            <Trans
              i18nKey="signupform.privacy"
              components={{
                reactLink: (
                  <Link
                    color="gray.darker"
                    href="mailto:privacy@roulezjeunesse.com"
                  />
                ),
              }}
            />
          </Text>
        );
        // Compact : le texte légal reste lisible, mais replié sous le bouton.
        return compact ? (
          <Depliant>
            <summary>{t('espacesite.donnees.titre')}</summary>
            {confidentialite}
          </Depliant>
        ) : (
          confidentialite
        );
      })()}
    </WrapperForm>
  );
};

export default SignupForm;
